import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBrowseListings } from '@steelbuy/data-access';
import { SearchResultsSorting } from '@steelbuy/ui-domain';
import { LoadingSpinner, Separator, SeparatorIdentifier } from '@steelbuy/ui-primitive';
import { SearchListingsSummary } from './SearchListingsSummary';
import { mapSearchToBrowseListingForm } from './SearchListingsUtil';
import { RoutePath } from '../../router/Routes';
import { TableLayout } from '../../views/layouts/table-layout/TableLayout';
import { SORT_OPTIONS } from '../common-utils/ListingTableUtils';
import { DashboardFormData, getDefaultDashboardFormData } from '../dashboard-listings.tsx/DashboardFormContext';
import { buildRequestBody } from '../dashboard-listings.tsx/dashboardListingsUtil';
import { useSearchFormDataContext } from '../listing-search/SearchFormDataContext';
import { SearchResultsPageHeader } from '../listing-search-results/SearchResultsPageHeader';
import { ListingsTable } from '../listings-table/ListingsTable';
import './SearchListings.scss';

export const SearchListings = () => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);

    const navigate = useNavigate();
    const searchFormData = useSearchFormDataContext().getSearchFormData();
    const formData = mapSearchToBrowseListingForm(searchFormData);
    const { sort } = getDefaultDashboardFormData();
    const methods = useForm<DashboardFormData>({
        defaultValues: {
            ...formData,
            sort,
        },
    });

    const { watch, getValues, setValue } = methods;

    const listingsQuery = useBrowseListings(buildRequestBody(getValues()));
    const {
        data: listings,
        total,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
    } = listingsQuery;

    const handleBackToSearch = (): void => {
        navigate(RoutePath.SEARCH_LISTINGS);
    };

    return (
        <div className="search-results-table">
            <SearchResultsPageHeader handleBackToSearch={handleBackToSearch} />
            <TableLayout>
                <FormProvider {...methods}>
                    <SearchListingsSummary />
                    <Separator separatorType={SeparatorIdentifier.ON_LIGHT} />
                    <div className="search-results-table-header">
                        <div className="search-results-table-header__results">
                            {total === undefined
                                ? ''
                                : t('application.dashboardListings.results.header', { count: total })}
                        </div>
                        <div className="search-results-table-header__actions">
                            <div />
                            <SearchResultsSorting
                                handleSortChange={(value) => setValue('sort', value)}
                                sortCriteria={watch('sort') ?? null}
                                isDisabled={total === 0}
                                selectedLabelMapper={(option) =>
                                    t(`application.dashboardListings.sortByOption`, { option: option.label })
                                }
                                showTitle={false}
                                sortOptions={SORT_OPTIONS}
                            />
                        </div>
                    </div>
                    {isLoading ? (
                        <LoadingSpinner fillContainerHeight />
                    ) : (
                        <ListingsTable
                            listings={listings}
                            formData={searchFormData}
                            hasNextPage={hasNextPage}
                            loadMore={fetchNextPage}
                            isFetching={isFetching}
                            isFetchingNextPage={isFetchingNextPage}
                            total={total}
                            detailsPath={RoutePath.SEARCH_LISTING_DETAILS}
                        />
                    )}
                </FormProvider>
            </TableLayout>
        </div>
    );
};
